/**
 * Client-side image downscaling for the banner upload flow. There's no
 * backend to receive an original-size upload, so we resize to a sane cap
 * before persisting to localStorage — keeps the stored data: URL well
 * under localStorage's per-origin quota. Cropping to the banner's aspect
 * ratio happens at render time via object-fit: cover, not here, so the
 * same stored image still looks right if the banner's aspect ratio ever
 * changes.
 */

const MAX_SOURCE_BYTES = 20 * 1024 * 1024; // 20MB — guard against pathological uploads

export function resizeImageFile(file, { maxWidth = 1600, maxHeight = 800, quality = 0.85 } = {}) {
  return new Promise((resolve, reject) => {
    if (!file?.type?.startsWith('image/')) {
      reject(new Error('Please choose an image file.'));
      return;
    }
    if (file.size > MAX_SOURCE_BYTES) {
      reject(new Error('That image is too large. Try one under 20MB.'));
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read that file.'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Could not load that image.'));
      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width, maxHeight / img.height);
        const width = Math.round(img.width * scale);
        const height = Math.round(img.height * scale);

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}
